import { SerializedData, Store } from "bernie-core";
import { action, observable } from "mobx";
import { Template, FlexBffTemplateResponse, ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ModuleDefinition } from "src/typings/flexFramework/FlexComponent";
import { NewBlossomComponent } from "components/flexFramework/domain/NewBlossomComponent";
import { BlossomComponentTree } from "components/flexFramework/domain/BlossomComponentTree";
import { Logger } from "bernie-logger";
import { BreadcrumbsFlexModuleResult } from "src/typings/microserviceModels/breadcrumbs-flex-module";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { CompositionStore } from "src/stores/CompositionStore";
import { getNameFromModuleDefinition } from "./getNameFromModuleDefinition";

export class TemplateStore extends Store {
  @observable
  public template: Template;

  @observable
  public blossomComponentTree: BlossomComponentTree;

  public breadcrumbsModel: BreadcrumbsFlexModuleResult;
  private blossomComponentsLogger: Logger;

  public constructor(state: SerializedData = {}, logger: Logger, blossomComponentsLogger: Logger) {
    super(state, logger);
    this.blossomComponentsLogger = blossomComponentsLogger;
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  /**
   * initializes TemplateStore with data from other stores. Called from controller
   * @param  {FlexBffTemplateResponse} bffResponse
   * @param  {ExtendedContextStore} context
   * @param  {FlexModuleModelStore} flexModuleModelStore
   * @returns void
   */
  @action
  public setup(
    bffResponse: FlexBffTemplateResponse,
    context: ExtendedContextStore,
    flexModuleModelStore: FlexModuleModelStore
  ) {
    if (bffResponse) {
      this.template = bffResponse.template;

      this.blossomComponentTree = new BlossomComponentTree(
        this.template.regions.reduce((result, child) => {
          const childBlossomComponent = this.moduleDefinitionToBlossomComponent(child, flexModuleModelStore, context);
          if (childBlossomComponent !== undefined) {
            result.push(childBlossomComponent);
          }

          return result;
        }, [] as NewBlossomComponent[])
      );
    }
  }

  /**
   * if necessary, hydrates serialized BlossomComponentTree and it's BlossomComponents, adding methods and wiring to FlexModuleModelStore
   * @param flexModuleModelStore
   * @param compositionStore
   * @param context
   * @return BlossomComponentTree
   */
  @action
  public hydrateBlossomComponentTree(
    flexModuleModelStore: FlexModuleModelStore,
    compositionStore: CompositionStore,
    context: ExtendedContextStore
  ): BlossomComponentTree {
    if (typeof this.blossomComponentTree.hydrateTree !== "function") {
      // TODO: remove call to populateFlexModuleModelStoreFromComposition when we can start serializing FlexModuleModelStore
      compositionStore.populateFlexModuleModelStoreFromComposition(flexModuleModelStore, context);
      this.blossomComponentTree = new BlossomComponentTree(this.blossomComponentTree.tree).hydrateTree(
        flexModuleModelStore
      );
    }

    return this.blossomComponentTree;
  }

  /**
   * creates NewBlossomComponent equivalent of provided ModuleDefintion
   * @param  {ModuleDefinition} moduleDefinition
   * @param  {FlexModuleModelStore} flexModuleModelStore
   * @param  {ExtendedContextStore} context
   * @returns NewBlossomComponent|undefined
   */
  private moduleDefinitionToBlossomComponent(
    moduleDefinition: ModuleDefinition,
    flexModuleModelStore: FlexModuleModelStore,
    context: ExtendedContextStore
  ): NewBlossomComponent | undefined {
    // create model object from all non-standard moduleDefinition properites
    const model = { ...moduleDefinition };
    const name = getNameFromModuleDefinition(moduleDefinition);

    if (name === "tab" || name === "region") {
      model.name = moduleDefinition.name;
    }

    delete model.id;
    delete model.fmId;
    delete model.fmTitleId;
    delete model.view;
    delete model.scene;
    delete model.modules;
    delete model.groups;
    delete model.children;

    // gather and create decendant BlossomComponents
    const childModuleDefinitions: ModuleDefinition[] = [
      ...(moduleDefinition.modules || []),
      ...(moduleDefinition.groups || []),
      ...(moduleDefinition.children || []),
    ];
    const children = childModuleDefinitions.reduce((result, child) => {
      const childBlossomComponent = this.moduleDefinitionToBlossomComponent(child, flexModuleModelStore, context);
      if (childBlossomComponent !== undefined) {
        result.push(childBlossomComponent);
      }

      return result;
    }, [] as NewBlossomComponent[]);

    return new NewBlossomComponent({
      componentData: {
        id: moduleDefinition.id,
        name,
        fmId: moduleDefinition.fmId,
        fmTitleId: moduleDefinition.fmTitleId,
        view: moduleDefinition.view,
        scene: moduleDefinition.scene,
        children,
        flexModuleModel: {
          responsePending: moduleDefinition.clientSideOnly ? false : true,
          error: false,
          model,
        },
      },
      context,
      flexModuleModelStore,
      logger: this.blossomComponentsLogger,
    });
  }

  /**
   * removes data that will be re-constructed client-side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    return super.toJSON("blossomComponentsLogger");
  }
}
